import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { tCrit, tStatus, crsBandCls, crsRing, crsBarColor, CRS_DIM_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import { CritBadge, StatusBadge } from "@/components/shared/Badges";
import ResilienceSummary from "@/components/shared/ResilienceSummary";
import {
  Server, Boxes, Share2, ScrollText, TrendingUp, ArrowRight,
  ShieldAlert, BadgeCheck, Gauge, ChevronDown, Check, X, HelpCircle,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
  PieChart, Pie,
} from "recharts";

const CRIT_COLORS = { critical: "#FF3B30", high: "#FF9F0A", medium: "#0A84FF", low: "#71717A" };

// Mapping reason_code (identifiants techniques STABLES, anglais) -> libellés/explications FR.
// Fallback « Motif non documenté » pour tout code inconnu (compatibilité future).
const ORG_REASON_LABELS = {
  "CRS-ORG-OK": "Le score organisationnel est calculable : au moins un service possède un CRS calculable.",
  "CRS-ORG-NC-NO-SERVICE": "Aucun service n'est présent dans le périmètre : le score organisationnel est NON CALCULABLE.",
  "CRS-ORG-NC-NO-CALCULABLE-SERVICE": "Des services existent mais aucun ne possède de CRS calculable : le score organisationnel est NON CALCULABLE.",
};
const ORG_PRQ_LABELS = {
  "CRS-ORG-PRQ-CALCULABLE-SERVICE": "Au moins un service possède un CRS calculable",
  "CRS-ORG-PRQ-CRITICAL-COVERAGE": "Tous les services critiques disposent d'un CRS",
  "CRS-ORG-PRQ-FULL-COVERAGE": "Tous les services du périmètre sont calculables",
};
const ORG_ACTION_LABELS = {
  "CRS-ORG-ACT-ADD-SERVICE": "Ajouter au moins un service au périmètre pour permettre le calcul.",
  "CRS-ORG-ACT-COVER-CRITICAL": "Prioriser une simulation what-if couvrant les services critiques non calculables.",
  "CRS-ORG-ACT-RUN-SIMULATION": "Exécuter une simulation pertinente pour les services non calculables afin d'obtenir leur CRS.",
};
const SVC_REASON_LABELS = {
  "CRS-SVC-OK": "CRS du service calculable.",
  "CRS-SVC-NC-NO-SIMULATION": "Aucune simulation pertinente n'a été exécutée pour ce service.",
  "CRS-SVC-NC-MISSING-DATA": "Données obligatoires insuffisantes pour calculer le CRS.",
};
const UNKNOWN_REASON = "Motif non documenté.";

// Regroupements visuels des 4 grands domaines CROS — navigation vers des routes EXISTANTES.
const DOMAINS = [
  {
    key: "cartographie", icon: Share2, to: "/dependencies", perm: "dependency:read",
    title: "Cartographie & résilience",
    desc: "Graphe Node/Edge, dépendances, SPOF et redondances vérifiées.",
  },
  {
    key: "menaces", icon: ShieldAlert, to: "/vulnerabilities", perm: "vulnerability:read",
    title: "Menaces & vulnérabilités",
    desc: "Vulnérabilités, menaces adverses et techniques MITRE ATT&CK.",
  },
  {
    key: "controles", icon: BadgeCheck, to: "/controls", perm: "control:read",
    title: "Contrôles & conformité",
    desc: "Contrôles, efficacité attestée et cadres de conformité.",
  },
  {
    key: "risques", icon: Gauge, to: "/risks", perm: "risk:read",
    title: "Risques & simulation",
    desc: "Risque inhérent, scénarios, simulation what-if et risque résiduel.",
  },
];

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div
      className="relative border border-border bg-card p-6 rounded-sm overflow-hidden transition-colors hover:border-primary/40"
      data-testid={testid}
    >
      <span className="absolute left-0 top-0 h-full w-0.5 bg-primary/60" aria-hidden="true" />
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
        <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
      </div>
      <div className="font-mono text-4xl font-semibold tracking-tight">{value}</div>
    </div>
  );
}

function DomainCard({ d, onOpen }) {
  return (
    <button
      onClick={() => onOpen(d.to)}
      data-testid={`domain-card-${d.key}`}
      className="group text-left rounded-sm border border-border bg-card p-6 transition-[transform,border-color,box-shadow] duration-200 hover:-translate-y-0.5 hover:border-primary/60 hover:shadow-lg hover:shadow-primary/10"
    >
      <div className="h-10 w-10 rounded-sm border border-primary/25 bg-primary/10 flex items-center justify-center mb-4">
        <d.icon className="h-5 w-5 text-primary" />
      </div>
      <h3 className="font-head font-semibold text-base group-hover:text-primary transition-colors">{d.title}</h3>
      <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{d.desc}</p>
      <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-primary">
        Explorer
        <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
      </span>
    </button>
  );
}

function OrgScoreCard() {
  const [open, setOpen] = useState(false);
  const [whyOpen, setWhyOpen] = useState(false);
  const prqDetail = (p) => {
    const v = p.values || {};
    if (p.code === "CRS-ORG-PRQ-CALCULABLE-SERVICE") return `${v.evaluated}/${v.total} service(s) calculable(s)`;
    if (p.code === "CRS-ORG-PRQ-CRITICAL-COVERAGE")
      return p.satisfied ? "aucun service critique sans CRS" : `${v.critical_gap} service(s) critique(s) non calculable(s)`;
    if (p.code === "CRS-ORG-PRQ-FULL-COVERAGE")
      return p.satisfied ? "aucun service non calculable" : `${v.non_calculable} non calculable(s) · couverture ${v.coverage_pct}%`;
    return "";
  };
  const { data, isLoading } = useQuery({
    queryKey: ["org-crs"],
    queryFn: async () => (await api.get("/resilience-scores/organization")).data,
  });
  return (
    <div className="relative border border-border bg-card rounded-sm overflow-hidden" data-testid="resilience-score-card">
      <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full blur-3xl pointer-events-none"
           style={{ background: "radial-gradient(circle, hsl(216 100% 50% / 0.10), transparent 70%)" }} aria-hidden="true" />
      <div className="relative p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-4 w-4 text-primary" />
          <span className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">
            Cyber Resilience Score — Organisation
          </span>
        </div>
        {isLoading ? (
          <div className="text-muted-foreground font-mono text-sm">Calcul en cours…</div>
        ) : data ? (
          <>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
              <div className="flex items-end gap-4">
                <div className="font-mono text-6xl font-semibold tracking-tight leading-none"
                     data-testid="org-crs-score" style={{ color: crsRing(data.level) }}>
                  {data.calculable ? data.final_score : "N/C"}
                  {data.calculable && <span className="text-2xl text-muted-foreground font-normal">/100</span>}
                </div>
                <div className="pb-1">
                  <span className={`text-[11px] font-mono uppercase px-2 py-1 rounded-sm border ${crsBandCls(data.level)}`}
                        data-testid="org-crs-level">{data.level}</span>
                  {data.calculable && (
                    <div className="text-xs text-muted-foreground mt-2 font-mono">brut {data.raw_score} · final {data.final_score}</div>
                  )}
                </div>
              </div>
              <div className="text-xs text-muted-foreground space-y-1 lg:text-right">
                <div>
                  <span className="font-mono text-foreground" data-testid="org-crs-evaluated">{data.services_evaluated}</span> calculable(s)
                  {" · "}
                  <span className="font-mono" data-testid="org-crs-noncalc">{data.services_non_calculable}</span> non calculable(s)
                </div>
                <div>Couverture pondérée <span className="font-mono text-foreground">{Math.round((data.weighted_coverage || 0) * 100)}%</span></div>
                <div>Confiance {data.confidence_level} · {data.confidence_index}/100</div>
              </div>
            </div>

            {/* Indicateur Calculable / Non calculable + panneau « Pourquoi ? » (explicabilité) */}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span
                data-testid="org-crs-status"
                className={`text-[11px] font-mono uppercase px-2 py-1 rounded-sm border ${
                  data.calculation_status === "CALCULABLE"
                    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                    : "border-amber-500/30 bg-amber-500/10 text-amber-400"
                }`}
              >
                {data.calculation_status === "CALCULABLE" ? "Calculable" : "Non calculable"}
              </span>
              <button
                onClick={() => setWhyOpen((o) => !o)}
                data-testid="org-crs-why-btn"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
              >
                <HelpCircle className="h-3.5 w-3.5" />
                Pourquoi ?
              </button>
            </div>

            {whyOpen && (
              <div className="mt-3 rounded-sm border border-border bg-background/40 p-4 space-y-3" data-testid="org-crs-why-panel">
                <p className="text-xs text-foreground" data-testid="org-crs-why-reason">
                  {ORG_REASON_LABELS[data.reason_code] || UNKNOWN_REASON}
                </p>
                <div className="space-y-1.5">
                  <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground">Prérequis</div>
                  {(data.prerequisites || []).map((p) => (
                    <div key={p.code} className="flex items-start gap-2 text-xs" data-testid={`org-crs-prq-${p.code}`}>
                      {p.satisfied
                        ? <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0 mt-0.5" data-testid={`org-crs-prq-ok-${p.code}`} />
                        : <X className="h-3.5 w-3.5 text-amber-400 shrink-0 mt-0.5" data-testid={`org-crs-prq-missing-${p.code}`} />}
                      <span className="text-foreground">{ORG_PRQ_LABELS[p.code] || UNKNOWN_REASON}</span>
                      <span className="text-muted-foreground">— {prqDetail(p)}</span>
                    </div>
                  ))}
                </div>
                {(data.recommended_actions || []).length > 0 && (
                  <div className="space-y-1.5">
                    <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground">Action recommandée</div>
                    {data.recommended_actions.map((a) => (
                      <div key={a} className="flex items-start gap-2 text-xs text-muted-foreground" data-testid={`org-crs-action-${a}`}>
                        <ArrowRight className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                        <span>{ORG_ACTION_LABELS[a] || UNKNOWN_REASON}</span>
                      </div>
                    ))}
                  </div>
                )}
                {data.status_summary && (
                  <div className="text-[11px] text-muted-foreground border-t border-border pt-2 font-mono" data-testid="org-crs-status-summary">
                    {data.status_summary.services_evaluated} calculable(s) · {data.status_summary.services_non_calculable} non calculable(s) · couverture {data.status_summary.weighted_coverage_pct}% · services critiques couverts {data.status_summary.critical_services_covered}/{data.status_summary.critical_services_total}
                  </div>
                )}
              </div>
            )}

            {data.calculable && data.binding_cap && (
              <div className="mt-4 flex flex-wrap items-center gap-2 text-xs" data-testid="org-crs-cap">
                <span className="font-mono px-2 py-1 rounded-sm border border-amber-500/30 bg-amber-500/10 text-amber-400">{data.binding_cap}</span>
                {(data.cap_reasons || []).filter((c) => c.binding).map((c) => (
                  <span key={c.cap} className="text-muted-foreground">
                    {c.reason} {c.services?.length ? `(${c.services.map((s) => s.name).join(", ")})` : ""}
                  </span>
                ))}
              </div>
            )}

            {(data.critical_services_below_threshold || []).length > 0 && (
              <div className="mt-3 text-xs text-muted-foreground" data-testid="org-crs-below">
                Services critiques / haute criticité sous seuil :{" "}
                {data.critical_services_below_threshold.map((s) => `${s.name} (${s.crs})`).join(" · ")}
              </div>
            )}

            <button onClick={() => setOpen((o) => !o)} data-testid="org-crs-drilldown"
                    className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-primary">
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
              {open ? "Masquer" : "Voir"} la contribution par service
            </button>

            {open && (
              <div className="mt-4 border-t border-border pt-4 space-y-2">
                {data.services_included.map((s) => (
                  <div key={s.service_id} className="flex items-center justify-between gap-3 text-xs"
                       data-testid={`org-crs-service-${s.service_id}`}>
                    <span className="truncate">
                      {s.name} <span className="text-muted-foreground capitalize">· {s.criticality} · poids {s.weight}</span>
                    </span>
                    <span className="flex items-center gap-3 font-mono shrink-0">
                      <span style={{ color: crsRing(s.level) }}>{s.crs}</span>
                      <span className="text-muted-foreground">contrib {s.contribution}</span>
                    </span>
                  </div>
                ))}
                {(data.services_excluded || []).map((s) => (
                  <div key={s.service_id} className="flex items-start justify-between gap-3 text-xs text-muted-foreground"
                       data-testid={`org-crs-excluded-${s.service_id}`}>
                    <span className="truncate">
                      {s.name} <span className="capitalize">· {s.criticality}</span>
                      <span className="block text-[10px] text-amber-400/80" data-testid={`org-crs-excluded-reason-${s.service_id}`}>
                        {SVC_REASON_LABELS[s.reason_code] || UNKNOWN_REASON}
                      </span>
                    </span>
                    <span className="font-mono shrink-0">NON CALCULABLE</span>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-muted-foreground">Indisponible.</div>
        )}
      </div>
    </div>
  );
}

function CrsCard({ s }) {
  const dims = ["D-STR", "D-VEX", "D-CTL", "D-SIM", "D-REC"];
  return (
    <div className="rounded-sm border border-border bg-card p-5 transition-colors hover:border-primary/30" data-testid={`crs-card-${s.service_id}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="font-head font-semibold text-sm truncate">{s.name}</div>
          <div className="text-xs text-muted-foreground capitalize mt-0.5">{s.criticality}</div>
        </div>
        <span className={`shrink-0 text-[10px] font-mono uppercase px-2 py-1 rounded-sm border ${crsBandCls(s.level)}`} data-testid={`crs-level-${s.service_id}`}>
          {s.level}
        </span>
      </div>
      <div className="flex items-end gap-3 mb-4">
        <div className="font-mono text-4xl font-semibold tracking-tight leading-none" data-testid={`crs-score-${s.service_id}`}
             style={{ color: crsRing(s.level) }}>
          {s.calculable ? s.crs : "N/C"}
          {s.calculable && <span className="text-base text-muted-foreground font-normal">/100</span>}
        </div>
        <div className="text-[11px] text-muted-foreground pb-1 leading-tight">
          <div>Confiance {s.confidence_level} · {s.confidence_index}/100</div>
          {s.binding_cap && <div className="text-amber-400 font-mono" data-testid={`crs-cap-${s.service_id}`}>{s.binding_cap}</div>}
          <div>{s.scenario_count} scénario(s)</div>
        </div>
      </div>
      <div className="space-y-1.5">
        {dims.map((d) => (
          <div key={d} className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground w-24 shrink-0">{CRS_DIM_LABELS[d]}</span>
            <div className="h-1.5 flex-1 rounded-full bg-zinc-800 overflow-hidden">
              <div className="h-full rounded-full transition-[width] duration-500"
                   style={{ width: `${s.subscores?.[d] ?? 0}%`, background: crsBarColor(s.subscores?.[d]) }} />
            </div>
            <span className="text-[10px] font-mono text-muted-foreground w-7 text-right">{s.subscores?.[d] ?? "—"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CrsSection() {
  const { data, isLoading } = useQuery({
    queryKey: ["crs-overview"],
    queryFn: async () => (await api.get("/resilience-scores/overview")).data,
  });
  return (
    <section className="space-y-4" data-testid="crs-section">
      <div>
        <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Cyber Resilience Score par service</h2>
        <p className="text-xs text-muted-foreground mt-1.5 max-w-3xl leading-relaxed">
          Score 0–100 (100 = meilleure résilience), explicable et non compensatoire — dérivé de la structure,
          de l'exposition, des contrôles, de la simulation et du rétablissement. Aucun score organisationnel global.
        </p>
      </div>
      {isLoading ? (
        <div className="text-muted-foreground font-mono text-sm">Calcul en cours…</div>
      ) : data && data.length ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.map((s) => <CrsCard key={s.service_id} s={s} />)}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">Aucun service à évaluer.</div>
      )}
    </section>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard/stats")).data,
  });

  const c = data?.counts || {};
  const critData = Object.entries(data?.asset_criticality || {}).map(([k, v]) => ({
    name: tCrit(k), value: v, fill: CRIT_COLORS[k] || "#71717A",
  }));
  const statusData = Object.entries(data?.asset_status || {}).map(([k, v]) => ({ name: tStatus(k), value: v }));
  const domains = DOMAINS.filter((d) => can(d.perm));

  return (
    <AppShell title="Tableau de bord exécutif" subtitle="Vue d'ensemble de la posture de cyber-résilience">
      {isLoading ? (
        <div className="text-muted-foreground font-mono text-sm">Chargement…</div>
      ) : (
        <div className="space-y-10">
          {/* ── Posture globale ── */}
          <section className="space-y-4">
            <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Posture globale</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Kpi icon={Server} label="Actifs" value={c.assets ?? 0} testid="kpi-assets" />
              <Kpi icon={Boxes} label="Services critiques" value={c.services ?? 0} testid="kpi-services" />
              <Kpi icon={Share2} label="Dépendances" value={c.dependencies ?? 0} testid="kpi-dependencies" />
              <Kpi icon={ScrollText} label="Entrées d'audit" value={c.audit_entries ?? 0} testid="kpi-audit" />
            </div>

            {/* Cyber Resilience Score ORGANISATIONNEL — agrégation pondérée par criticité, non compensatoire */}
            <OrgScoreCard />
          </section>

          {/* ── Cyber Resilience Score par service ── */}
          <CrsSection />

          {/* ── Domaines CROS ── */}
          {domains.length > 0 && (
            <section className="space-y-4">
              <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Domaines CROS</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {domains.map((d) => <DomainCard key={d.key} d={d} onOpen={navigate} />)}
              </div>
            </section>
          )}

          {/* ── Résilience structurelle (moteur — pas de score global) ── */}
          <section className="space-y-4">
            <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Analyse structurelle</h2>
            <ResilienceSummary />
          </section>

          {/* ── Répartition des actifs ── */}
          <section className="space-y-4">
            <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Répartition des actifs</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="border border-border bg-card p-6 rounded-sm lg:col-span-2">
                <h3 className="font-head font-semibold mb-6">Actifs par criticité</h3>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={critData}>
                    <XAxis dataKey="name" stroke="#71717A" fontSize={12} tickLine={false} />
                    <YAxis stroke="#71717A" fontSize={12} allowDecimals={false} tickLine={false} />
                    <Tooltip
                      cursor={{ fill: "#ffffff08" }}
                      contentStyle={{ background: "#121214", border: "1px solid #27272A", borderRadius: 4 }}
                    />
                    <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                      {critData.map((e) => <Cell key={e.name} fill={e.fill} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="border border-border bg-card p-6 rounded-sm">
                <h3 className="font-head font-semibold mb-6">Statut des actifs</h3>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3}>
                      {statusData.map((e, i) => (
                        <Cell key={e.name} fill={["#32D74B", "#FF9F0A", "#FF3B30", "#71717A"][i % 4]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#121214", border: "1px solid #27272A", borderRadius: 4 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="border border-border bg-card rounded-sm">
              <h3 className="font-head font-semibold p-6 pb-4 border-b border-border">Actifs les plus critiques</h3>
              <div className="divide-y divide-border">
                {(data?.top_critical_assets || []).length === 0 && (
                  <p className="p-6 text-sm text-muted-foreground">Aucun actif de criticité élevée ou critique.</p>
                )}
                {(data?.top_critical_assets || []).map((a) => (
                  <div key={a.id} className="flex items-center justify-between px-6 py-3">
                    <span className="text-sm font-medium">{a.name}</span>
                    <div className="flex items-center gap-2">
                      <CritBadge value={a.criticality} />
                      <StatusBadge value={a.status} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      )}
    </AppShell>
  );
}
